import { useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { getInitials } from '@/shared/lib/get-initials';

export interface UserAvatarProps {
  name: string;
  /** When set with `avatarUrl`, used as the `<img>` `alt` (e.g. person name while `name` drives initials). */
  imageAlt?: string;
  avatarUrl?: string;
  sizeClassName: string;
  textClassName: string;
  /** Load immediately for above-the-fold avatars (header, profile hero). Defaults to lazy. */
  priority?: boolean;
}

interface AvatarPhotoProps {
  avatarUrl: string;
  imageAlt: string;
  priority: boolean;
  onLoaded: () => void;
  onFailed: () => void;
}

/** Remounts when `avatarUrl` changes so load state resets without an effect. */
function AvatarPhoto({
  avatarUrl,
  imageAlt,
  priority,
  onLoaded,
  onFailed,
}: AvatarPhotoProps) {
  const [loaded, setLoaded] = useState(false);

  return (
    <img
      src={avatarUrl}
      alt={imageAlt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...(priority ? { fetchPriority: 'high' as const } : {})}
      onLoad={() => {
        setLoaded(true);
        onLoaded();
      }}
      onError={onFailed}
      className={cn(
        'absolute inset-0 h-full w-full rounded-full object-cover transition-opacity duration-200',
        loaded ? 'opacity-100' : 'opacity-0',
      )}
    />
  );
}

export function UserAvatar({
  name,
  imageAlt,
  avatarUrl,
  sizeClassName,
  textClassName,
  priority = false,
}: UserAvatarProps) {
  const initials = getInitials(name);
  const trimmedUrl = avatarUrl?.trim();
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showPhoto = Boolean(trimmedUrl) && failedUrl !== trimmedUrl;

  return (
    <span className={cn('relative inline-flex shrink-0 overflow-hidden rounded-full', sizeClassName)}>
      <span
        aria-hidden={showPhoto ? true : undefined}
        className={cn(
          'flex h-full w-full items-center justify-center rounded-full bg-brand-100 font-semibold uppercase text-brand-800',
          textClassName,
        )}
      >
        {initials || '?'}
      </span>
      {showPhoto && trimmedUrl ? (
        <AvatarPhoto
          key={trimmedUrl}
          avatarUrl={trimmedUrl}
          imageAlt={imageAlt ?? name}
          priority={priority}
          onLoaded={() => {
            setFailedUrl(null);
          }}
          onFailed={() => {
            if (trimmedUrl) {
              setFailedUrl(trimmedUrl);
            }
          }}
        />
      ) : null}
    </span>
  );
}
